import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Plus, ArrowUpRight, ArrowDownRight, RotateCcw, FileText, Clock, Package } from 'lucide-react';

const LINE_STATUS_COLORS = {
  Active: 'bg-emerald-500/15 text-emerald-400',
  Returned: 'bg-gray-500/15 text-gray-400',
  Upgraded: 'bg-blue-500/15 text-blue-400',
  Downgraded: 'bg-amber-500/15 text-amber-400',
  Sold: 'bg-red-500/15 text-red-400',
};

const EVENT_ICONS = {
  DELIVERY: <Package className="h-4 w-4 text-emerald-400" />,
  PICKUP: <RotateCcw className="h-4 w-4 text-gray-400" />,
  UPGRADE: <ArrowUpRight className="h-4 w-4 text-blue-400" />,
  DOWNGRADE: <ArrowDownRight className="h-4 w-4 text-amber-400" />,
  ASSET_ADDED: <Plus className="h-4 w-4 text-emerald-400" />,
  ASSET_REMOVED: <RotateCcw className="h-4 w-4 text-red-400" />,
  RATE_CHANGE: <FileText className="h-4 w-4 text-purple-400" />,
};

export default function ContractDetailPage() {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [events, setEvents] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddAsset, setShowAddAsset] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(null);
  const [showDowngrade, setShowDowngrade] = useState(null);
  const [newAssetId, setNewAssetId] = useState('');
  const [newRate, setNewRate] = useState('');
  const [addAssetId, setAddAssetId] = useState('');
  const [addRate, setAddRate] = useState('');
  const [tab, setTab] = useState('lines');

  const fetchData = async () => {
    try {
      const [cRes, eRes, aRes] = await Promise.all([
        api.get(`/contracts/${contractId}`),
        api.get(`/contracts/${contractId}/events`),
        api.get('/assets'),
      ]);
      setContract(cRes.data);
      setEvents(Array.isArray(eRes.data) ? eRes.data : []);
      setAssets(Array.isArray(aRes.data) ? aRes.data : []);
    } catch (e) { console.error(e); toast.error('Failed to load contract'); navigate('/contracts'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [contractId]);

  const availableAssets = assets.filter(a => {
    const s = a.asset_status === 'Active' ? 'Available' : a.asset_status;
    return s === 'Available';
  });

  const handleAddAsset = async () => {
    if (!addAssetId) { toast.error('Select an asset'); return; }
    try {
      await api.post(`/contracts/${contractId}/add-line`, { asset_id: addAssetId, monthly_rate: addRate ? Number(addRate) : null });
      toast.success('Asset added');
      setShowAddAsset(false); setAddAssetId(''); setAddRate('');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handlePickup = async (lineId, assetId) => {
    if (!window.confirm(`Pick up asset ${assetId}?`)) return;
    try {
      await api.post(`/contracts/${contractId}/pickup/${lineId}`);
      toast.success('Asset picked up');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleUpgrade = async (lineId) => {
    if (!newAssetId) { toast.error('Select new asset'); return; }
    try {
      await api.post(`/contracts/${contractId}/upgrade/${lineId}`, { asset_id: newAssetId, monthly_rate: newRate ? Number(newRate) : null });
      toast.success('Asset upgraded');
      setShowUpgrade(null); setNewAssetId(''); setNewRate('');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleDowngrade = async (lineId) => {
    if (!newAssetId) { toast.error('Select new asset'); return; }
    try {
      await api.post(`/contracts/${contractId}/downgrade/${lineId}`, { asset_id: newAssetId, monthly_rate: newRate ? Number(newRate) : null });
      toast.success('Asset downgraded');
      setShowDowngrade(null); setNewAssetId(''); setNewRate('');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await api.put(`/contracts/${contractId}`, { status: newStatus });
      toast.success(`Contract set to ${newStatus}`);
      fetchData();
    } catch (e) { toast.error('Failed to update'); }
  };

  if (loading) return <div className="flex justify-center items-center h-64"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!contract) return null;

  const activeLines = (contract.contract_lines || []).filter(l => l.status === 'Active');
  const closedLines = (contract.contract_lines || []).filter(l => l.status !== 'Active');

  return (
    <div className="space-y-6" data-testid="contract-detail">
      <Button variant="ghost" onClick={() => navigate('/contracts')} className="mb-2"><ArrowLeft className="h-4 w-4 mr-2" />Back to Contracts</Button>

      <Card>
        <CardContent className="p-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold font-mono">{contract.contract_number}</h1>
                <Badge variant="outline" className={LINE_STATUS_COLORS[contract.status] || ''}>{contract.status}</Badge>
              </div>
              <div className="text-lg">{contract.customer_name}</div>
              <div className="text-sm text-muted-foreground">{contract.start_date} to {contract.end_date} &middot; {contract.billing_cycle} billing</div>
              {contract.notes && <div className="text-sm text-muted-foreground italic">{contract.notes}</div>}
            </div>
            <div className="text-right space-y-2">
              <div className="text-xs text-muted-foreground">Monthly Value</div>
              <div className="text-2xl font-bold text-primary">₹{(contract.total_monthly_value || 0).toLocaleString()}</div>
              <div className="flex gap-2">
                {contract.status === 'Draft' && <Button size="sm" onClick={() => handleStatusChange('Active')}>Activate</Button>}
                {contract.status === 'Active' && <Button size="sm" variant="outline" onClick={() => handleStatusChange('Completed')}>Complete</Button>}
                {contract.status === 'Active' && <Button size="sm" variant="destructive" onClick={() => handleStatusChange('Terminated')}>Terminate</Button>}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex gap-1 border-b">
        {['lines', 'events'].map(t => (
          <button key={t} className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
            onClick={() => setTab(t)}>
            {t === 'lines' ? `Asset Lines (${(contract.contract_lines || []).length})` : `Events (${events.length})`}
          </button>
        ))}
      </div>

      {tab === 'lines' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="font-semibold">Active Assets ({activeLines.length})</h2>
            <Button size="sm" onClick={() => setShowAddAsset(true)} data-testid="add-asset-btn"><Plus className="h-4 w-4 mr-1" />Add Asset</Button>
          </div>
          {activeLines.map(l => (
            <Card key={l.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-bold">{l.asset_id}</span>
                      <Badge variant="outline" className={LINE_STATUS_COLORS[l.status]}>{l.status}</Badge>
                      <Badge variant="outline">{l.asset_type}</Badge>
                    </div>
                    <div className="text-sm text-muted-foreground">{l.model_info} &middot; SN: {l.serial_number}</div>
                    <div className="text-xs text-muted-foreground">Since {l.start_date} &middot; ₹{l.monthly_rate}/mo</div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline" onClick={() => { setShowUpgrade(l.id); setNewAssetId(''); setNewRate(''); }}><ArrowUpRight className="h-3 w-3 mr-1" />Upgrade</Button>
                    <Button size="sm" variant="outline" onClick={() => { setShowDowngrade(l.id); setNewAssetId(''); setNewRate(''); }}><ArrowDownRight className="h-3 w-3 mr-1" />Downgrade</Button>
                    <Button size="sm" variant="destructive" onClick={() => handlePickup(l.id, l.asset_id)}><RotateCcw className="h-3 w-3 mr-1" />Pickup</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {closedLines.length > 0 && (
            <>
              <h2 className="font-semibold text-muted-foreground mt-6">Closed Lines ({closedLines.length})</h2>
              {closedLines.map(l => (
                <Card key={l.id} className="opacity-60">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm">{l.asset_id}</span>
                      <Badge variant="outline" className={LINE_STATUS_COLORS[l.status]}>{l.status}</Badge>
                      <span className="text-xs text-muted-foreground">{l.start_date} — {l.end_date}</span>
                      <span className="text-xs">₹{l.monthly_rate}/mo</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </>
          )}
        </div>
      )}

      {tab === 'events' && (
        <div className="space-y-3">
          {events.length === 0 && <div className="text-center py-8 text-muted-foreground">No events yet</div>}
          {events.map(e => (
            <div key={e.id} className="flex items-start gap-3 p-3 rounded-lg border hover:bg-muted/30">
              <div className="mt-1">{EVENT_ICONS[e.event_type] || <Clock className="h-4 w-4" />}</div>
              <div className="flex-1 space-y-1">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">{e.event_type}</Badge>
                  <span className="font-mono text-xs">{e.asset_id}</span>
                  {e.new_asset_id && <><span className="text-xs">→</span><span className="font-mono text-xs">{e.new_asset_id}</span></>}
                </div>
                {e.old_rate != null && e.new_rate != null && (
                  <div className="text-xs text-muted-foreground">Rate: ₹{e.old_rate} → ₹{e.new_rate}</div>
                )}
                <div className="text-xs text-muted-foreground">By {e.actor} &middot; {new Date(e.timestamp).toLocaleString()}</div>
                {e.notes && <div className="text-xs italic">{e.notes}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showAddAsset} onOpenChange={setShowAddAsset}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add Asset to Contract</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Asset</Label>
              <Select value={addAssetId} onValueChange={setAddAssetId}>
                <SelectTrigger><SelectValue placeholder="Select available asset" /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {availableAssets.map(a => <SelectItem key={a.asset_id} value={a.asset_id}>{a.asset_id} - {a.brand} {a.model_name} ({a.asset_type})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Monthly Rate (leave blank for default)</Label>
              <Input type="number" value={addRate} onChange={e => setAddRate(e.target.value)} placeholder="Default rate" />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowAddAsset(false)}>Cancel</Button>
              <Button onClick={handleAddAsset}>Add Asset</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!showUpgrade} onOpenChange={() => setShowUpgrade(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upgrade Asset</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>New Asset</Label>
              <Select value={newAssetId} onValueChange={setNewAssetId}>
                <SelectTrigger><SelectValue placeholder="Select replacement asset" /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {availableAssets.map(a => <SelectItem key={a.asset_id} value={a.asset_id}>{a.asset_id} - {a.brand} {a.model_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>New Monthly Rate (leave blank for default)</Label>
              <Input type="number" value={newRate} onChange={e => setNewRate(e.target.value)} />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowUpgrade(null)}>Cancel</Button>
              <Button onClick={() => handleUpgrade(showUpgrade)}>Confirm Upgrade</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!showDowngrade} onOpenChange={() => setShowDowngrade(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Downgrade Asset</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>New Asset</Label>
              <Select value={newAssetId} onValueChange={setNewAssetId}>
                <SelectTrigger><SelectValue placeholder="Select replacement asset" /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {availableAssets.map(a => <SelectItem key={a.asset_id} value={a.asset_id}>{a.asset_id} - {a.brand} {a.model_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>New Monthly Rate (leave blank for default)</Label>
              <Input type="number" value={newRate} onChange={e => setNewRate(e.target.value)} />
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowDowngrade(null)}>Cancel</Button>
              <Button onClick={() => handleDowngrade(showDowngrade)}>Confirm Downgrade</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
